import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchAllProfilingMaster,
  fetchOneProfilingMaster,
  fetchProfilingMasterByParty,
  createProfilingMaster,
  updateProfilingMaster,
  deleteProfilingMaster,
} from "services/profilingMaster";

export const useAllProfilingMaster = (filters = {}, options = {}) => {
  const { enabled = true, refetchOnWindowFocus = true } = options;
  const query = useQuery({
    queryKey: ["all-profiling-master", filters],
    queryFn: () => fetchAllProfilingMaster(filters),
    enabled,
    refetchOnWindowFocus,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
  return query;
};

export const useOneProfilingMaster = (id, options = {}) => {
  const { enabled = true, refetchOnWindowFocus = true } = options;
  const query = useQuery({
    queryKey: ["one-profiling-master", id],
    queryFn: () => fetchOneProfilingMaster(id),
    enabled: enabled && !!id,
    refetchOnWindowFocus,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
  return query;
};

export const useProfilingMasterByParty = (ps_Id, options = {}) => {
  const { enabled = true, refetchOnWindowFocus = false } = options;
  const query = useQuery({
    queryKey: ["profiling-master-by-party", ps_Id],
    queryFn: () => fetchProfilingMasterByParty(ps_Id),
    enabled: enabled && !!ps_Id,
    refetchOnWindowFocus,
    staleTime: 5 * 60 * 1000, // 5 minutes - cache longer for bill usage
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 (no profiling master found)
      if (error?.response?.status === 404) {
        return false;
      }
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
  });
  return query;
};

export const useCreateProfilingMaster = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProfilingMaster,
    onSuccess: () => {
      // Invalidate all profiling master queries
      queryClient.invalidateQueries({ queryKey: ["all-profiling-master"] });

      // Invalidate by-party queries (new profiling master created)
      queryClient.invalidateQueries({ 
        queryKey: ["profiling-master-by-party"] 
      });
    },
  });
};

export const useUpdateProfilingMaster = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateProfilingMaster(id, data),
    onSuccess: (data, variables) => {
      // Invalidate all profiling master queries
      queryClient.invalidateQueries({ queryKey: ["all-profiling-master"] });
      
      // Invalidate specific profiling master query
      queryClient.invalidateQueries({ 
        queryKey: ["one-profiling-master", variables.id] 
      });

      // Invalidate by-party queries (since ps_Id might have changed)
      queryClient.invalidateQueries({ 
        queryKey: ["profiling-master-by-party"] 
      });
    },
  });
};

export const useDeleteProfilingMaster = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProfilingMaster,
    onSuccess: (data, id) => {
      // Invalidate all profiling master queries
      queryClient.invalidateQueries({ queryKey: ["all-profiling-master"] });
      
      // Remove specific profiling master query from cache
      queryClient.removeQueries({ 
        queryKey: ["one-profiling-master", id] 
      });

      // Invalidate by-party queries (profiling master deleted)
      queryClient.invalidateQueries({ 
        queryKey: ["profiling-master-by-party"] 
      });
    },
  });
}; 